/* ============================================================================
   Smooth-scroll bridge — Lenis
   ----------------------------------------------------------------------------
   Opt-in per page. GSAP handles time; ScrollTrigger handles scroll position;
   Lenis smooths what the browser reports as scroll position so that scrubbed
   timelines and pinned sections settle rather than snap.

   Load Lenis from a pinned CDN in the page's <head>, next to GSAP:

     <script src="https://cdn.jsdelivr.net/npm/lenis@1.1.13/dist/lenis.min.js"></script>

   Then, in the experiment's main.js, after initMotion():

     import { initSmoothScroll } from '../../src/scroll.js';
     initSmoothScroll(ScrollTrigger);

   Tokens spent:
     --mo-scroll-lerp   how much the smooth layer catches up per frame.
     --mo-slow          upper bound used when Lenis' own duration is asked for.

   Reduced motion disables Lenis entirely — the page falls back to native
   scroll, which is the accessible default and also the honest one to review.
   ========================================================================= */

import { isReduced, MOTION, refreshMotionTokens } from './motion.js';

let lenis = null;
let rafId = null;

export function initSmoothScroll(ScrollTrigger) {
  if (isReduced()) return null;
  if (typeof window === 'undefined' || !window.Lenis) {
    console.info('[scroll] Lenis not found on window — smooth scroll off. Add the CDN <script>.');
    return null;
  }
  if (lenis) return lenis; // idempotent — mounting twice would double-scroll

  refreshMotionTokens();
  lenis = new window.Lenis({
    lerp: MOTION.scrollLerp ?? readLerp(),
    duration: MOTION.dur.slow,
    smoothWheel: true,
    smoothTouch: false, // native touch is already smooth; forcing it fights momentum
  });

  // ScrollTrigger reads window.scrollY on every frame. Tell it to refresh so
  // pinned/scrubbed sections track Lenis's virtual scroll rather than fighting
  // it. Documented Lenis pattern.
  const tick = (time) => {
    lenis.raf(time);
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);

  if (ScrollTrigger) {
    lenis.on('scroll', ScrollTrigger.update);
    // Make gsap.ticker the driver — cheaper than two independent RAFs.
    window.gsap?.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    window.gsap?.ticker.lagSmoothing(0);
    // Once tokens or reduced-motion state flip, re-measure.
    window.addEventListener('mo:replay', () => {
      lenis?.resize();
      ScrollTrigger.refresh();
    });
    // Kill the standalone RAF now that gsap.ticker owns the loop.
    cancelAnimationFrame(rafId);
    rafId = null;
  }

  // If Reduced gets toggled on later, tear down cleanly.
  window.addEventListener('mo:replay', () => {
    if (isReduced()) destroySmoothScroll();
  });

  return lenis;
}

/** Read the current lerp token straight from the DOM; kept out of MOTION so
 *  the value is always the live one, no cache. */
function readLerp() {
  const css = getComputedStyle(document.documentElement);
  return parseFloat(css.getPropertyValue('--mo-scroll-lerp')) || 0.1;
}

export function getLenis() {
  return lenis;
}

export function destroySmoothScroll() {
  if (!lenis) return;
  lenis.destroy();
  lenis = null;
  if (rafId != null) cancelAnimationFrame(rafId);
  rafId = null;
}

/** Scroll to a selector or Y position, respecting Lenis if mounted. */
export function scrollTo(target, options = {}) {
  if (lenis) {
    lenis.scrollTo(target, options);
    return;
  }
  const y =
    typeof target === 'number'
      ? target
      : (typeof target === 'string' ? document.querySelector(target) : target)?.getBoundingClientRect()
          .top + window.scrollY || 0;
  window.scrollTo({ top: y, behavior: options.immediate ? 'auto' : 'smooth' });
}
